import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import WaitInputs from "./WaitInputs";
import {
  getMerchantByMerchantId,
  getWaitListByMerchantId,
  getLastUpdateTime,
} from "@/apis";
import { setMerchant } from "@/store/slices/merchant";
import { setWaitlist } from "@/store/slices/waitlist";
import { showAlert } from "@/store/slices/feedback";
import { isTimeInBzHours } from "@/utils";

const useStyles = makeStyles({
  root: {},
  name: {
    fontSize: "20px",
    fontFamily: "Roboto",
    color: "#fff",
    textAlign: "center",
    marginBottom: 31,
    height: 23,
  },
});

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { merchantId } = useParams();
  const [firstload, setFirstload] = useState(false);
  const [heartbeat, setHeartbeat] = useState(Date.now());
  const [disabled, setDisabled] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const merchant = useSelector((state) => state.merchant);
  const waitlist = useSelector((state) => state.waitlist.list);
  const currentWaitData = getCurrentWait(merchant.tables, waitlist);

  useEffect(() => {
    if (!firstload) {
      onFirstload();
    }
    const interval = setInterval(async () => {
      const now = Date.now();
      const refreshInterval = 1000 * 5;
      if (now > heartbeat + refreshInterval) {
        try {
          if (merchant) {
            const { businessHours, isOpen, timeZone } = merchant;
            handleWaitlistOpenStatus(businessHours, isOpen, timeZone);
          }
          let fetchLastUpdateTime = await getLastUpdateTime(merchantId);
          const newLastUpdate = fetchLastUpdateTime.data.lastUpdateTime;
          if (lastUpdateTime !== newLastUpdate) {
            setLastUpdateTime(newLastUpdate);
            onLoad();
          }
        } catch (err) {
          console.log(err);
          dispatch(showAlert("Failed to load data. "));
        }
        setHeartbeat(now);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };

    function handleWaitlistOpenStatus(businessHours, isOpen, timeZone) {
      if (!isTimeInBzHours(businessHours, timeZone)) {
        dispatch(showAlert("Out of business hour."));
        setDisabled(true);
      } else if (!isOpen) {
        dispatch(showAlert("The Waitlist is currently closed. "));
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    }
    async function onLoad() {
      const [merchant, waitlist] = await Promise.all([
        getMerchantByMerchantId(merchantId),
        getWaitListByMerchantId(merchantId),
      ]);
      dispatch(setMerchant(merchant.data));
      dispatch(setWaitlist({ list: waitlist.data }));
      const { businessHours, isOpen, timeZone } = merchant.data;
      handleWaitlistOpenStatus(businessHours, isOpen, timeZone);
    }
    async function onFirstload() {
      await onLoad();
      setFirstload(true);
    }
  });

  return (
    <div className={classes.root}>
      <div className={classes.name}>{merchant ? merchant.name : ""}</div>
      <CurrentWait data={currentWaitData} />
      <WaitInputs disabled={disabled} />
    </div>
  );
};

function getCurrentWait(tableTypes, waitlist) {
  const hasSizeTable = tableTypes.filter((each) => each.min);
  const tabs = hasSizeTable.map((table) => {
    const { name, min, max } = table;
    let sizeStr = `${min}`;
    if (max) {
      sizeStr = `${sizeStr}~${max}`;
    } else {
      sizeStr = `${sizeStr}+`;
    }
    const label = `${name}(${sizeStr})`;
    const count = waitlist.filter(
      (wait) => wait.tableTypeId === table.id
    ).length;
    return { label, id: table.id, count };
  });

  return tabs;
}

const useCurrentWaitStyles = makeStyles({
  root: {
    margin: "11px 20px",
    height: 198,
    padding: "19px 19px",
  },
  tables: {
    display: "flex",
    margin: "33px 0px",
    justifyContent: "space-around",
  },
  table: {
    width: 100,
    height: 100,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "0 16px",
    borderRight: "1px solid rgba(210,209,215,.4)",
    flex: 1,
    "&:last-of-type": {
      borderRight: "none",
    },
  },
  waitText: {
    color: "#6c727c",
  },
  labelText: {
    color: "#1c1939",
    textTransform: "capitalize",
  },
});
function CurrentWait({ data }) {
  const classes = useCurrentWaitStyles();
  return (
    <Paper className={classes.root}>
      <span className={classes.waitText}>Current Wait:</span>
      <div className={classes.tables}>
        {data.map((table, i) => (
          <div key={table.id} className={classes.table}>
            <div>{table.count}</div>
            <div className={classes.labelText}>{table.label}</div>
          </div>
        ))}
      </div>
    </Paper>
  );
}
